// Upload one document onto an approved intake route.
//
// The browser half of POST /v1/tenants/{t}/intake, and nothing more. What this
// does NOT do is the point of it:
//
//   * It does not choose the administration. The office chose that when it
//     approved the route; the reviewer picks a ROUTE, and the API answers with
//     the administration the route resolved to. See apps/api/routes/intake.ts.
//   * It does not decide what the file is. The type it sends is what the browser
//     claims, and the API records it beside the type it sniffed from the bytes —
//     which is the one it believes.
//   * It does not say the document was read. A 202 is "received"; reading happens
//     in the worker, and the document's own state says how far it got.

import { useT, type Translate } from '../i18n/index.tsx';
import { useCallback, useEffect, useState, type ChangeEvent, type DragEvent, type ReactNode } from 'react';
import { Icon } from './Icon.tsx';
import { ApiError, request, tenantPath } from '../api/client.ts';
import type { IntakeAccepted, IntakeRoute, IntakeRoutes } from '../api/types.ts';

/**
 * The largest file this sends: base64 grows it by a third, and the API refuses
 * a body over BK_API_BODY_LIMIT_BYTES with 413. Refusing here first says so in
 * words instead of as a transport error.
 */
export const MAX_UPLOAD_BYTES = 16 * 1024 * 1024;

export interface UploadDocumentProps {
  readonly tenantId: string;
  readonly token: string;
  /** Called with the new document's id once the API has accepted it. */
  readonly onUploaded: (documentId: string) => void;
}

export function UploadDocument({ tenantId, token, onUploaded }: UploadDocumentProps): ReactNode {
  const t = useT();
  const [routes, setRoutes] = useState<readonly IntakeRoute[] | null>(null);
  const [routeId, setRouteId] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);
  const [over, setOver] = useState(false);

  const loadRoutes = useCallback(async () => {
    try {
      const body = await request<IntakeRoutes>(tenantPath(tenantId, '/intake-routes'), { token });
      const usable = body.routes.filter((route) => route.channel === 'upload' && route.revokedAt === null);
      setRoutes(usable);
      setRouteId((current) => current || (usable[0]?.id ?? ''));
    } catch {
      setRoutes([]);
    }
  }, [tenantId, token]);

  useEffect(() => {
    void loadRoutes();
  }, [loadRoutes]);

  const choose = (event: ChangeEvent<HTMLInputElement>) => {
    setMessage(null);
    setFile(event.target.files?.[0] ?? null);
  };

  const upload = async (chosen: File | null = file) => {
    const file = chosen;
    if (file === null || routeId === '') return;
    if (file.size > MAX_UPLOAD_BYTES) {
      setMessage({ tone: 'error', text: t('upload.tooLarge', { size: megabytes(file.size), limit: megabytes(MAX_UPLOAD_BYTES) }) });
      return;
    }
    setBusy(true);
    setMessage(null);
    // One identity for this delivery: a retry of the same click is the same
    // arrival (ch.05 T03), a second click on the same file is a second one.
    const delivery = `upload-${crypto.randomUUID()}`;
    try {
      const accepted = await request<IntakeAccepted>(tenantPath(tenantId, '/intake'), {
        token,
        method: 'POST',
        idempotencyKey: delivery,
        body: {
          routeId,
          sourceEventRef: delivery,
          ...(file.type === '' ? {} : { declaredMediaType: file.type }),
          content: { encoding: 'base64', data: await base64(file) },
        },
      });
      setMessage({
        tone: 'ok',
        text:
          accepted.state === 'quarantined'
            ? t('upload.held', { blockers: accepted.blockers.join(', ') })
            : t('upload.received'),
      });
      setFile(null);
      onUploaded(accepted.documentId);
    } catch (error) {
      setMessage({ tone: 'error', text: refusal(error, t) });
    } finally {
      setBusy(false);
    }
  };

  // A dropped file is the same request as a chosen one: the same route, the
  // same delivery identity rule, the same size check. Dropping is the click.
  const drop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setOver(false);
    if (busy || routes === null || routes.length === 0) return;
    const dropped = event.dataTransfer.files[0] ?? null;
    if (dropped === null) return;
    setMessage(null);
    setFile(dropped);
    void upload(dropped);
  };

  if (routes === null) return null;

  return (
    <div
      className={`drop${over ? ' over' : ''}`}
      data-testid="upload-drop"
      onDragOver={(event) => {
        event.preventDefault();
        if (routes.length > 0) setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={drop}
    >
      <Icon name="upload" />
      <div>
        <b>{t('upload.drop')}</b>
      </div>
      <div className="small" style={{ marginTop: 2, marginBottom: 10 }}>
        {t('upload.kinds')}
      </div>
      <div>
        {routes.length === 0 ? (
          <p className="muted small">
            {t('upload.noRoute')}
          </p>
        ) : (
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            <label className="small">
              {t('upload.route')}{' '}
              <select value={routeId} onChange={(event) => setRouteId(event.target.value)} aria-label={t('upload.routeLabel')}>
                {routes.map((route) => (
                  <option key={route.id} value={route.id}>
                    {route.sourceAddress}
                  </option>
                ))}
              </select>
            </label>
            <input
              type="file"
              aria-label={t('upload.fileLabel')}
              accept="application/pdf,image/jpeg,image/png,text/plain,.txt,.sta,.mt940,.940,.swi"
              onChange={choose}
            />
            <button type="button" className="btn primary" disabled={busy || file === null || routeId === ''} onClick={() => void upload()}>
              {busy ? t('upload.busy') : t('upload.button')}
            </button>
          </div>
        )}
        {message === null ? null : (
          <p className={message.tone === 'ok' ? 'small' : 'error-banner'} role={message.tone === 'ok' ? 'status' : 'alert'} style={{ marginTop: 10 }}>
            {message.text}
          </p>
        )}
      </div>
    </div>
  );
}

function megabytes(bytes: number): string {
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** The file as base64, without the data-URL prefix. */
function base64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const url = String(reader.result);
      resolve(url.slice(url.indexOf(',') + 1));
    };
    reader.onerror = () => reject(reader.error ?? new Error('the file could not be read'));
    reader.readAsDataURL(file);
  });
}

/** What the API refused, in words; never a guess at a reason it did not give. */
function refusal(error: unknown, t: Translate): string {
  if (!(error instanceof ApiError)) return t('upload.failed');
  if (error.status === 413) return t('upload.tooLargeApi');
  if (error.status === 0) return t('upload.unreachable');
  const fields = error.fieldErrors.map((field) => `${field.field}: ${field.code}`).join(', ');
  return t('upload.refused', { code: error.code, fields });
}
